import { useEffect, useState, useRef, useCallback } from "react";
import { app, authentication } from "@microsoft/teams-js";
import { useAuth } from "react-oidc-context";
import { useUser } from "../context/UserContext";
import { useSession } from "../context/SessionContext";
import { useModel } from "../context/ModelContext"; 
import {
  createOrUpdateCurrentUser,
  getMyDetails,
  setAuthToken,
  setReAuthFunction
} from "../service/apiService";
import { toast } from "sonner";

export type AuthMode = 'teams' | 'cognito' | 'none';

const cleanupOldOidcState = () => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('oidc.')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn(`Failed to remove ${key}:`, e);
      }
    });
    if (keysToRemove.length > 0) {
      console.log(`Cleaned up ${keysToRemove.length} old OIDC state entries`);
    }
  } catch (error) {
    console.error('Error during OIDC cleanup:', error);
  }
};

// Extracted authentication handlers to make them reusable and exportable
export const authenticateWithTeams = async (
  setAuthToken: any,
  setToken: any,
  setAuthenticated: any,
  setUserId: any,
  setUserDisplayName: any,
  setUserDetails: any,
  fetchAndSetSessions: any,
  fetchAndSetModels: any,
  setAuthMode: any,
  setUserStatus: any,
  userFetchedRef: any,
  setIsLoading: any
) => {
  try {
    await app.initialize();
    const context = await app.getContext();
    const token = await authentication.getAuthToken({
      resources: ["https://graph.microsoft.com"],
      silent: true,
    });
    
    // Decode the JWT token to extract the expiration time
    const tokenParts = token.split('.');
    if (tokenParts.length === 3) {
      try {
        const payload = JSON.parse(atob(tokenParts[1]));
        if (payload.exp) {
          // Store the expiration time in sessionStorage
          sessionStorage.setItem('authTokenExp', payload.exp.toString());
        }
      } catch (decodeError) {
        console.error("Error decoding token:", decodeError);
      }
    }
    
    const userPrincipalName = (context.user?.userPrincipalName || "").toLowerCase();
    const displayName = context.user?.displayName || "";
    
    setAuthToken(token);
    setToken(token);
    setAuthenticated(true);
    setUserId(userPrincipalName);
    setUserDisplayName(displayName);
    setAuthMode('teams');

    if (userPrincipalName) {
      await Promise.all([
        createOrUpdateCurrentUser(),
        fetchAndSetSessions(userPrincipalName),
        fetchAndSetModels(),
      ]);
      
      const userDetails = await getMyDetails();
      setUserDetails(userDetails);
      setUserStatus(userDetails.user_status);
    }

    if (userFetchedRef) {
      userFetchedRef.current = true;
    }
    if (setIsLoading) {
      setIsLoading(false);
    }

    window.dispatchEvent(new Event('authSuccess'));
    
    return { success: true };
  } catch (error) {
    console.error("Teams auth error:", error);
    if (error instanceof Error && error.message.includes('disabled')) {
      setAuthenticated(false);
      setToken("");
      setAuthToken("");
      setUserStatus('disabled');
      toast.error(error.message);
    }
    
    if (setIsLoading) {
      setIsLoading(false);
    }
    setAuthenticated(false);
    
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "Authentication failed" 
    };
  }
};

export const authenticateWithCognito = async (
  cognitoUser: any,
  setAuthToken: any,
  setToken: any,
  setAuthenticated: any,
  setUserId: any,
  setUserDisplayName: any,
  setUserDetails: any,
  fetchAndSetSessions: any,
  fetchAndSetModels: any,
  setAuthMode: any,
  setUserStatus: any,
  userFetchedRef: any,
  setIsLoading: any
) => {
  try {
    const cognitoToken = cognitoUser.id_token;

    // Decode JWT token to extract expiration time
    const tokenParts = cognitoToken.split('.');
    if (tokenParts.length === 3) {
      try {
        const payload = JSON.parse(atob(tokenParts[1]));
        if (payload.exp) {
          sessionStorage.setItem('authTokenExp', payload.exp.toString());
        }
      } catch (decodeError) {
        console.error("Error decoding Cognito token:", decodeError);
      }
    }

    setAuthToken(cognitoToken);
    setToken(cognitoToken);
    setAuthenticated(true);
    setUserId((cognitoUser.profile.email || "").toLowerCase());
    setUserDisplayName(cognitoUser.profile.name || "");
    setAuthMode('cognito');

    await Promise.all([
      createOrUpdateCurrentUser(),
      fetchAndSetSessions((cognitoUser.profile.email || "").toLowerCase()),
      fetchAndSetModels(),
    ]);

    const userDetails = await getMyDetails();
    setUserDetails(userDetails);
    setUserStatus(userDetails.user_status);
    
    if (userFetchedRef) {
      userFetchedRef.current = true;
    }
    if (setIsLoading) {
      setIsLoading(false);
    }
    
    window.dispatchEvent(new Event('authSuccess'));
    
    return { success: true };
  } catch (error) {
    console.error("Cognito auth error:", error);
    
    // Only show toast for actual auth failures, not redirects
    if (!(error instanceof Error && error.message.includes("login required"))) {
      toast.error("Authentication failed");
    }
    
    setAuthenticated(false);
    setUserStatus('disabled');
    
    if (setIsLoading) {
      setIsLoading(false);
    }
    
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "Authentication failed" 
    };
  }
};

// Modified to ensure the redirect is executed, not just returned
export const initiateAuthFlow = (cognitoAuth: any) => {
  try {
    cleanupOldOidcState();
    console.log('Executing Cognito sign-in redirect...');
    cognitoAuth.signinRedirect().catch((error: any) => {
      console.error('Redirect execution error:', error);
      if (error?.message?.includes('quota') || error?.message?.includes('QuotaExceededError')) {
        cleanupOldOidcState();
        cognitoAuth.signinRedirect().catch((retryError: any) => {
          console.error('Retry after cleanup failed:', retryError);
        });
      }
    });
    return { success: true };
  } catch (error) {
    console.error('Failed to initiate auth flow:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "Failed to initiate authentication" 
    };
  }
};

export function useUnifiedAuth() {
  const {
    setUserId,
    setUserDetails,
    setUserDisplayName,
    setAuthenticated,
    setToken,
  } = useUser();
  const { fetchAndSetSessions } = useSession();
  const { fetchAndSetModels } = useModel();
  const cognitoAuth = useAuth();
  
  const [isLoading, setIsLoading] = useState(true);
  const [authMode, setAuthMode] = useState<AuthMode>('none');
  const [userStatus, setUserStatus] = useState<'enabled' | 'disabled'>('enabled');
  const userFetched = useRef<boolean>(false);
  const redirectAttempted = useRef<boolean>(false);
  
  // Wrapper functions that use the extracted authentication handlers
  const handleTeamsAuth = async () => {
    const result = await authenticateWithTeams(
      setAuthToken,
      setToken,
      setAuthenticated,
      setUserId,
      setUserDisplayName,
      setUserDetails,
      fetchAndSetSessions,
      fetchAndSetModels,
      setAuthMode,
      setUserStatus,
      userFetched,
      setIsLoading
    );
    if (result.success) {
      toast.success("Authentication successful!");
    }
    return result;
  };

  const handleCognitoAuth = async (user: any) => {    
    const result = await authenticateWithCognito(
      user,
      setAuthToken,
      setToken,
      setAuthenticated,
      setUserId,
      setUserDisplayName,
      setUserDetails,
      fetchAndSetSessions,
      fetchAndSetModels,
      setAuthMode,
      setUserStatus,
      userFetched,
      setIsLoading
    );
    
    if (result.success) {
      toast.success("Authentication successful!");
    }
    return result;
  };

  // Force re-authentication method - use useCallback to stabilize the function reference
  const reAuthenticate = useCallback(async () => {
    // Reset notification flag for re-authentication
    if (authMode === 'teams') {
      console.log("re-authenticating teams");
      return handleTeamsAuth();
    } else if (authMode === 'cognito') {
      if (cognitoAuth.isAuthenticated && cognitoAuth.user) {
        console.log("re-authenticating cognito");
        return handleCognitoAuth(cognitoAuth.user);
      } else {
        // Execute the redirect, don't just return it
        return initiateAuthFlow(cognitoAuth);
      }
    }
    return { success: false, error: "No active authentication mode" };
  }, [authMode, cognitoAuth.isAuthenticated, cognitoAuth.user]);

  // Set the reAuthenticate function in the API service
  useEffect(() => {
    setReAuthFunction(reAuthenticate);
  }, [reAuthenticate]);

  // Teams authentication attempt - only run once
  useEffect(() => {
    const initTeamsAuth = async () => {
      if (userFetched.current) return;

      try {
        const result = await handleTeamsAuth();
        if (!result.success) {
          console.log("Teams authentication failed, switching to Cognito:", result.error);
          setAuthMode('cognito');
          setIsLoading(false);
        }
      } catch (error) {
        console.log("Not in Teams environment or Teams auth failed:", error);
        setAuthMode('cognito');
        setIsLoading(false);
      }
    };

    initTeamsAuth();
    // Empty dependency array to ensure this runs only once
  }, []);

  // Cognito authentication handling - modified to prevent duplicate processing
  useEffect(() => {
    if (authMode !== 'cognito') return;
    
    console.log('Cognito auth state:', { 
      isLoading: cognitoAuth.isLoading,
      isAuthenticated: cognitoAuth.isAuthenticated,
      hasError: !!cognitoAuth.error,
      hasUser: !!cognitoAuth.user,
      activeNavigator: cognitoAuth.activeNavigator,
      userFetched: userFetched.current,
      redirectAttempted: redirectAttempted.current,
    });
    
    if (cognitoAuth.isLoading) {
      setIsLoading(true);
      return;
    }

    if (cognitoAuth.error) {
      console.error('Cognito auth error:', cognitoAuth.error);
      
      // Only attempt redirect if we haven't already
      if (!redirectAttempted.current) {
        redirectAttempted.current = true;
        console.log('Attempting redirect after error...');
        initiateAuthFlow(cognitoAuth);
      } else {
        setIsLoading(false);
      }
      return;
    }

    // Only process authentication once
    if (cognitoAuth.isAuthenticated && cognitoAuth.user && !userFetched.current) {
      handleCognitoAuth(cognitoAuth.user);
      return;
    }

    // Critical fix: Ensure we attempt redirect only once and when needed
    if (!cognitoAuth.isAuthenticated && 
        !cognitoAuth.activeNavigator && 
        !userFetched.current && 
        !redirectAttempted.current) {
      console.log('Initiating Cognito sign-in redirect...');
      redirectAttempted.current = true;
      initiateAuthFlow(cognitoAuth);
    }
  }, [
    authMode,
    cognitoAuth.isLoading,
    cognitoAuth.isAuthenticated,
    cognitoAuth.error,
    cognitoAuth.user,
    cognitoAuth.activeNavigator
  ]);

  // Reset flags if auth mode changes
  useEffect(() => {
    if (authMode === 'none') {
      redirectAttempted.current = false;
      userFetched.current = false;
    }
  }, [authMode]);

  return {
    isLoading: isLoading || cognitoAuth.isLoading,
    authMode,
    isAuthenticated: authMode === 'teams' || cognitoAuth.isAuthenticated,
    error: authMode === 'cognito' ? cognitoAuth.error : null,
    userStatus,
    isEnabled: userStatus === 'enabled',
    reAuthenticate
  };
}